import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { gql } from "graphql-request";
import { graphqlClient } from "@/lib/graphql";
import { useAuth } from "@/hooks/useAuth";
import type { User, Listing, Collection } from "@/types";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { ListingCard } from "@/components/listings/ListingCard";
import { usePageTitle } from "@/hooks/usePageTitle";

const USER_QUERY = gql`
  query User($id: ID!, $sortBy: String) {
    user(id: $id) {
      id
      displayName
      bio
      avatarUrl
      rating
      ratingsCount
      createdAt
      activeListingsCount
      soldListingsCount
      city
    }
    userCollections(userId: $id) {
      id
      name
      coverImageUrl
      listingsCount
      createdAt
    }
    listings(sellerId: $id, limit: 48, sortBy: $sortBy) {
      id
      price
      condition
      conditionLabel
      status
      createdAt
      imageUrls
      displayTitle
      displayImageUrl
      shippingAvailable
      shippingCost
      location
      language
      languageLabel
      boundingBox {
        yMin
        xMin
        yMax
        xMax
      }
      siblingListings {
        id
        status
        gameTitle
        price
        boundingBox {
          yMin
          xMin
          yMax
          xMax
        }
      }
      game {
        id
        title
        imageUrl
        thumbnailUrl
        bggRating
        displayRating
        ratingSource
      }
      seller {
        id
        displayName
        avatarUrl
        rating
        ratingsCount
      }
      fomoBadges {
        savingsVsShops
        savingsPercent
        cheapestShopPrice
        uniqueInMarketplace
        wishlistCount
        recent
        priceDropped
        previousPrice
      }
    }
  }
`;

function memberSinceLabel(iso: string): string {
  const date = new Date(iso);
  const now = new Date();
  const months =
    (now.getFullYear() - date.getFullYear()) * 12 +
    (now.getMonth() - date.getMonth());
  if (months < 1) return "narys nuo šio mėn.";
  if (months < 12) return `narys ${months} mėn.`;
  const years = Math.floor(months / 12);
  return `narys ${years} m.`;
}

const SORT_OPTIONS: { value: string; label: string }[] = [
  { value: "newest", label: "Naujausi" },
  { value: "price_asc", label: "Pigiausi pirma" },
  { value: "price_desc", label: "Brangiausi pirma" },
];

export function UserProfilePage() {
  const { id } = useParams<{ id: string }>();
  const { user: currentUser } = useAuth();
  const [sortBy, setSortBy] = useState<string>("newest");

  const { data, isLoading } = useQuery({
    queryKey: ["user", id, sortBy],
    queryFn: () =>
      graphqlClient.request<{
        user: User | null;
        userCollections: Collection[];
        listings: Listing[];
      }>(USER_QUERY, { id, sortBy }),
    enabled: !!id,
  });

  const user = data?.user;

  usePageTitle(user?.displayName);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <LoadingSpinner />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-16 text-center sm:px-6 lg:px-8">
        <p className="text-gray-500">Vartotojas nerastas</p>
      </div>
    );
  }

  const collections = data?.userCollections ?? [];
  const listings = data?.listings ?? [];
  const sold = user.soldListingsCount ?? 0;
  const active = user.activeListingsCount ?? 0;
  const isMe = currentUser?.id === user.id;

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
      {/* Profile header */}
      <div className="card overflow-hidden p-6">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-start">
          {/* Avatar */}
          {user.avatarUrl ? (
            <img
              src={user.avatarUrl}
              alt={user.displayName}
              className="h-24 w-24 shrink-0 rounded-full object-cover"
            />
          ) : (
            <div className="flex h-24 w-24 shrink-0 items-center justify-center rounded-full bg-primary-800 text-3xl font-bold text-white">
              {user.displayName[0].toUpperCase()}
            </div>
          )}

          <div className="flex-1">
            <div className="flex flex-wrap items-baseline gap-3">
              <h1 className="text-2xl font-bold text-gray-900">
                {user.displayName}
              </h1>
              <span className="text-sm text-gray-500">
                {memberSinceLabel(user.createdAt)}
              </span>
              {user.city && (
                <span className="inline-flex items-center gap-1 text-sm text-gray-600">
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  {user.city}
                </span>
              )}
            </div>

            {user.bio && (
              <p className="mt-3 text-sm leading-relaxed text-gray-700">
                {user.bio}
              </p>
            )}

            {/* CTAs */}
            <div className="mt-5 flex flex-wrap gap-2">
              {!isMe && (
                <Link
                  to={
                    currentUser
                      ? `/messages?seller=${user.id}`
                      : `/login?redirect_to=${encodeURIComponent(`/messages?seller=${user.id}`)}`
                  }
                  className="inline-flex items-center gap-2 rounded-xl bg-primary-800 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-700"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  Rašyti pardavėjui
                </Link>
              )}
              {isMe && (
                <Link
                  to="/profile"
                  className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Redaguoti profilį
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Trust stats grid */}
        <div className="mt-6 grid grid-cols-3 gap-2 border-t border-gray-100 pt-5 text-center sm:grid-cols-3">
          <div>
            <div className="flex items-center justify-center gap-1">
              <svg className="h-5 w-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              <p className="text-2xl font-bold text-gray-900">
                {user.rating != null ? user.rating.toFixed(1) : "—"}
              </p>
            </div>
            <p className="mt-1 text-xs uppercase tracking-wide text-gray-500">
              {user.ratingsCount > 0
                ? `Įvertinimai (${user.ratingsCount})`
                : "Be įvertinimų"}
            </p>
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">{sold}</p>
            <p className="mt-1 text-xs uppercase tracking-wide text-gray-500">
              Parduota
            </p>
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">{active}</p>
            <p className="mt-1 text-xs uppercase tracking-wide text-gray-500">
              Aktyvūs
            </p>
          </div>
        </div>
      </div>

      {/* Collections */}
      {collections.length > 0 && (
        <div className="mt-8">
          <h2 className="mb-4 text-lg font-bold text-gray-900">Kolekcijos</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {collections.map((collection) => (
              <Link
                key={collection.id}
                to={`/collections/${collection.id}`}
                className="card overflow-hidden transition-shadow hover:shadow-lg"
              >
                <div className="aspect-video overflow-hidden bg-gray-100">
                  {collection.coverImageUrl ? (
                    <img
                      src={collection.coverImageUrl}
                      alt={collection.name}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-gray-400">
                      <svg className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                    </div>
                  )}
                </div>
                <div className="p-3">
                  <p className="truncate font-semibold text-gray-900">
                    {collection.name}
                  </p>
                  <p className="mt-0.5 text-xs text-gray-500">
                    {collection.listingsCount} skelbimų
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Active listings */}
      <div className="mt-8">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-lg font-bold text-gray-900">
            Aktyvūs skelbimai ({active})
          </h2>
          {listings.length > 1 && (
            <label className="flex items-center gap-2 text-sm text-gray-600">
              <span>Rikiuoti:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                {SORT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
        {listings.length > 0 ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {listings.map((listing) => (
              <ListingCard key={listing.id} listing={listing} />
            ))}
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-gray-300 py-12 text-center text-sm text-gray-500">
            Šis vartotojas šiuo metu neturi aktyvių skelbimų.
          </div>
        )}
      </div>
    </div>
  );
}
